// ─────────────────────────────────────────────────────────────
// CommentThread.js
// Self-contained comment thread component.
//
// Architecture notes:
//   - Comments stored in Firestore `comments` collection
//   - Each comment has an `threadId` field. Currently always
//     "global" — change to an eventId later for per-event threads
//     with zero data migration needed.
//   - Identity: name from localStorage + silent fingerprint
//   - One-time fetch on load (not real-time) — cheaper on Firestore
//   - Newest comments on top
//   - Day-based time dividers; 7+ day gaps show "N weeks ago"
//   - Client-side profanity filter (hardcoded list)
//   - Admin can delete comments and ban fingerprints
//   - Paginated: shows PAGE_SIZE comments, load-more button for rest
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection, addDoc, getDocs, getDoc, deleteDoc, setDoc,
  query, where, orderBy, limit, startAfter,
  Timestamp, doc
} from 'firebase/firestore';
import { getBrowserFingerprint } from '../utils/fingerprint';
import { banKey } from '../utils/banKey';

// ─── CONFIG ──────────────────────────────────────────────────
const THREAD_ID = 'global';   // Change to eventId for per-event threads
const INITIAL_SIZE = 5;        // Comments shown on first load
const MORE_SIZE = 10;          // Comments loaded per "load more" click

// ─── PROFANITY FILTER ─────────────────────────────────────────
// Hardcoded list of blocked words. Add to this list as needed.
// Uses whole-word matching to avoid false positives.
const BLOCKED_WORDS = [
  'fuck', 'shit', 'cunt', 'nigger', 'nigga', 'faggot', 'fag',
  'asshole', 'bitch', 'bastard', 'whore', 'slut', 'cock', 'dick',
  'pussy', 'retard', 'kike', 'spic', 'chink', 'wetback', 'tranny'
];

const containsProfanity = (text) => {
  const lower = text.toLowerCase();
  return BLOCKED_WORDS.some(word => {
    const re = new RegExp(`\\b${word}\\b`, 'i');
    return re.test(lower);
  });
};

// ─── TIME HELPERS ─────────────────────────────────────────────
const getRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
};



// ─── COMPONENT ───────────────────────────────────────────────
function CommentThread({ adminUser, showToast, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [name, setName] = useState(localStorage.getItem('rsvp_user_name') || '');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
  const textareaRef = useRef(null);

  // ─── LOAD COMMENTS ─────────────────────────────────────────
  const loadComments = async () => {
    setLoading(true);
    try {
      // Get total count for the thread
      const countSnap = await getDocs(
        query(collection(db, 'comments'), where('threadId', '==', THREAD_ID))
      );
      setTotalCount(countSnap.size);
      if (onCountChange) onCountChange(countSnap.size);

      // Load first page (newest first)
      const q = query(
        collection(db, 'comments'),
        where('threadId', '==', THREAD_ID),
        orderBy('createdAt', 'desc'),
        limit(INITIAL_SIZE)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setComments(docs);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === INITIAL_SIZE && countSnap.size > INITIAL_SIZE);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'comments'),
        where('threadId', '==', THREAD_ID),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(MORE_SIZE)
      );
      const snap = await getDocs(q);
      const more = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setComments(prev => [...prev, ...more]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === MORE_SIZE);
    } catch (error) {
      console.error('Error loading more comments:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { loadComments(); }, []);

  // ─── SUBMIT ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!name.trim()) {
      showToast('Please enter your name', 'error');
      return;
    }
    if (!commentText.trim()) {
      showToast('Please write something', 'error');
      return;
    }
    if (commentText.trim().length > 1000) {
      showToast('Comment must be under 1000 characters', 'error');
      return;
    }
    if (containsProfanity(commentText)) {
      showToast('Your comment contains language that is not allowed', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const fingerprint = await getBrowserFingerprint();

      // Check block list by hashed doc ID — blockedUsers is not listable by
      // unauthenticated visitors (it holds fingerprints, IPs and names), so this
      // checks only the caller's own key. See src/utils/banKey.js.
      const blockedSnap = await getDoc(doc(db, 'blockedUsers', await banKey('fp', fingerprint)));
      if (blockedSnap.exists()) {
        showToast('You have been blocked from posting.', 'error');
        return;
      }

      localStorage.setItem('rsvp_user_name', name.trim());

      const newComment = {
        threadId: THREAD_ID,
        name: name.trim(),
        text: commentText.trim(),
        fingerprint,
        isAdmin: !!adminUser,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'comments'), newComment);

      // Optimistically prepend to list
      setComments(prev => [{ id: docRef.id, ...newComment }, ...prev]);
      setTotalCount(prev => { const n = prev + 1; if (onCountChange) onCountChange(n); return n; });
      setCommentText('');
      setComposeOpen(false);
      showToast('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      showToast('Error posting comment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── ADMIN: DELETE COMMENT ──────────────────────────────────
  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    const deleted = comments.find(c => c.id === commentId);
    try {
      await deleteDoc(doc(db, 'comments', commentId));
      setComments(prev => prev.filter(c => c.id !== commentId));
      setTotalCount(prev => { const n = prev - 1; if (onCountChange) onCountChange(n); return n; });
      showToast('Comment deleted');
      await addDoc(collection(db, 'auditLogs'), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: 'comment_deleted',
        entityType: 'comment',
        entityId: commentId,
        entityLabel: deleted?.text ? deleted.text.slice(0, 80) : null,
        before: deleted ? { name: deleted.name || null, text: deleted.text || null } : null,
        after: null,
        timestamp: Timestamp.now()
      });
    } catch (error) {
      showToast('Error deleting comment', 'error');
    }
  };

  // ─── ADMIN: BAN USER ────────────────────────────────────────
  const handleBan = async (fingerprint, commentName) => {
    if (!window.confirm(`Ban "${commentName}"? They won't be able to post comments or RSVPs.`)) return;
    try {
      await setDoc(doc(db, 'blockedUsers', await banKey('fp', fingerprint)), {
        fingerprint,
        name: commentName,
        blockedAt: Timestamp.now(),
        blockedBy: adminUser.email
      });
      await addDoc(collection(db, 'auditLogs'), {
        actorType: 'admin',
        actorId: adminUser.email,
        actorName: adminUser.email,
        action: 'user_banned_from_comments',
        entityType: 'blockedUser',
        entityId: fingerprint,
        entityLabel: commentName,
        before: null,
        after: { fingerprint, name: commentName },
        timestamp: Timestamp.now()
      });
      showToast(`Banned "${commentName}"`);
    } catch (error) {
      showToast('Error banning user', 'error');
    }
  };

  // ─── RENDER COMMENTS WITH DIVIDERS ──────────────────────────
  // Comments are newest-first. Dividers appear between comments
  // when the calendar day changes or there's a 7+ day gap.
  // commentIndex tracks alternating row colors across dividers.
  const renderComments = () => {
    if (comments.length === 0) {
      return <p className="comment-empty">No comments yet. Be the first!</p>;
    }

    const items = [];
    let commentIndex = 0;

    // Helper to render a single comment row
    const renderRow = (comment, commentDate) => {
      const altClass = commentIndex % 2 === 0 ? '' : ' comment-item-alt';
      commentIndex++;
      return (
        <div key={`comment-${comment.id}`} className={`comment-item${altClass}`}>
          <div className="comment-header">
            <span className="comment-name">{comment.name}</span>
            {comment.isAdmin && (
              <span className="comment-admin-badge">Admin</span>
            )}
            <span className="comment-time">{getRelativeTime(commentDate)}</span>
            {adminUser && (
              <span className="comment-admin-actions">
                <button className="admin-rsvp-btn delete"
                  onClick={() => handleDelete(comment.id)} title="Delete">✕</button>
                <button className="admin-rsvp-btn block"
                  onClick={() => handleBan(comment.fingerprint, comment.name)} title="Ban user">⊘</button>
              </span>
            )}
          </div>
          <div className="comment-text">{comment.text}</div>
        </div>
      );
    };

    comments.forEach((comment, i) => {
      const commentDate = comment.createdAt.toDate();

      // Render this comment
      items.push(renderRow(comment, commentDate));


    });

    return items;
  };

  // ─── MAIN RENDER ─────────────────────────────────────────────
  return (
    <div className="comment-thread">
      {/* Compose — collapsed by default, expands on click */}
      <button
        className={`comment-compose-toggle${composeOpen ? ' open' : ''}`}
        onClick={() => {
          setComposeOpen(prev => !prev);
          if (!composeOpen) setTimeout(() => textareaRef.current?.focus(), 50);
        }}
      >
        <span>{composeOpen ? 'Cancel' : '+ Add comment'}</span>
      </button>

      {composeOpen && (
        <div className="comment-compose">
          <input
            type="text"
            className="comment-name-input"
            placeholder="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={50}
          />
          <textarea
            ref={textareaRef}
            className="comment-textarea"
            placeholder="Write a comment…"
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            maxLength={1000}
            rows={3}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
            }}
          />
          <div className="comment-compose-footer">
            <span className="comment-char-count">
              {commentText.length > 800 ? `${1000 - commentText.length} chars left` : ''}
            </span>
            <button
              className="comment-submit-btn"
              onClick={handleSubmit}
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? '...' : 'Post'}
            </button>
          </div>
        </div>
      )}

      {/* Comment list — wrapper div enforces width constraint on desktop */}
      {loading ? (
        <p className="comment-empty">Loading comments...</p>
      ) : (
        <div className="comment-list">
          {renderComments()}
          {hasMore && (
            <button className="comment-load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading...' : `Load more (${totalCount - comments.length} remaining)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default CommentThread;
